import { HttpErrorResponse } from "@angular/common/http";
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService } from "../../../../auth/auth.service";
import { ConfirmModalComponent, ConfirmModalData } from "../../../../components/confirm-modal/confirm-modal.component";
import { SnackbarService } from "../../../../components/snackbar/snackbar.service";
import { UserFormComponent } from "../../components/user-form/user-form.component";
import { UpdateUserRequest, UserDetail, UserFormValue } from "../../models/user.model";
import { UserApiService } from "../../services/user.api.service";

@Component({
    selector: "app-edit-user",
    templateUrl: "./edit-user.component.html",
    styleUrl: "./edit-user.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [UserFormComponent]
})
export class EditUserComponent {
    private readonly userService = inject(UserApiService);
    private readonly authService = inject(AuthService);
    private readonly dialog = inject(MatDialog);
    private readonly router = inject(Router);
    private readonly snackbarService = inject(SnackbarService);
    private readonly destroyRef = inject(DestroyRef);

    private readonly username = inject(ActivatedRoute).snapshot.paramMap.get("userId") ?? "";

    readonly user = signal<UserDetail | null>(null);
    readonly submitting = signal(false);

    private readonly currentUserEmail = signal<string | null>(null);

    readonly isSelf = computed(() => {
        const signedIn = this.currentUserEmail()?.toLowerCase();
        return !!signedIn && signedIn === this.user()?.email.toLowerCase();
    });

    readonly canDelete = computed(() => this.user() !== null && this.currentUserEmail() !== null && !this.isSelf());

    constructor() {
        // The router has no scroll restoration configured, so navigating here from a scrolled
        // user list would open this page part-way down. Start at the top instead.
        window.scrollTo(0, 0);

        this.userService
            .getUser(this.username)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: user => this.user.set(user),
                error: (error: HttpErrorResponse) => {
                    this.snackbarService.show(error.error?.detail ?? "We couldn’t load this user. Try again.", "error");
                    void this.router.navigate(["/admin/users"]);
                }
            });

        this.authService
            .getCurrentUserEmail()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(email => this.currentUserEmail.set(email));
    }

    onSubmit(value: UserFormValue): void {
        const { role, tenantIds, firstName, lastName } = value;

        const request: UpdateUserRequest = { role, tenantIds };
        if (firstName !== undefined) request.firstName = firstName;
        if (lastName !== undefined) request.lastName = lastName;

        this.submitting.set(true);
        this.userService.updateUser(this.username, request).subscribe({
            next: () => {
                this.snackbarService.show("User saved.", "success");
                void this.router.navigate(["/admin/users"]);
            },
            error: (error: HttpErrorResponse) => {
                this.submitting.set(false);
                this.snackbarService.show(error.error?.detail ?? "We couldn’t save your changes. Try again.", "error");
            }
        });
    }

    onDelete(): void {
        if (!this.canDelete()) return;

        const email = this.user()?.email ?? "";

        const data: ConfirmModalData = {
            title: "Delete User?",
            message: [
                "This will delete ",
                { text: email, bold: true },
                " and remove their access to all assigned shows and channels."
            ],
            confirmLabel: "Delete user",
            danger: true
        };

        this.dialog
            .open<ConfirmModalComponent, ConfirmModalData, boolean>(ConfirmModalComponent, { data })
            .afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(confirmed => {
                if (confirmed) this.deleteUser();
            });
    }

    private deleteUser(): void {
        const email = this.user()?.email ?? "";

        this.submitting.set(true);
        this.userService.deleteUser(this.username).subscribe({
            next: () => {
                this.snackbarService.show(`${email} has been deleted.`, "success");
                void this.router.navigate(["/admin/users"]);
            },
            error: (error: HttpErrorResponse) => {
                this.submitting.set(false);
                this.snackbarService.show(error.error?.detail ?? "We couldn’t delete this user. Try again.", "error");
            }
        });
    }
}
